import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ShieldCheck,
  ScanLine,
  Share2,
  LifeBuoy,
  Store,
  Wrench,
  Bike,
  Car,
  BadgePercent,
  Truck,
  Briefcase,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { MaskLines, FadeIn, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { BadgeSoon, BadgeLive, SectionHead, BtnOrange, BtnGhost } from "@/components/bits";
import { usePreRegister } from "@/components/PreRegisterModal";
import { IMAGES } from "@/data/site";

const SERVICES = [
  { icon: Car, title: "360Taxi", desc: "Serviço da primeira fase.", live: true },
  { icon: BadgePercent, title: "Descontos", desc: "Em breve.", live: false },
  { icon: Truck, title: "Entregas", desc: "Em breve.", live: false },
  { icon: Wrench, title: "Serviços", desc: "Em breve.", live: false },
  { icon: Sparkles, title: "Oportunidades", desc: "Em breve.", live: false },
];

const SAFETY = [
  { icon: ShieldCheck, t: "Verificação de motoristas", d: "Cadastro e análise documental dos motoristas parceiros." },
  { icon: ScanLine, t: "Código de embarque", d: "Identificação para iniciar a viagem com segurança." },
  { icon: Share2, t: "Compartilhamento de viagem", d: "Envie o acompanhamento da corrida a pessoas de confiança." },
  { icon: LifeBuoy, t: "Canais de segurança e suporte", d: "Acesso aos canais oficiais de ajuda dentro do aplicativo." },
];

const FUTURE = [
  { icon: Store, title: "Comércio e Descontos", desc: "Estabelecimentos próximos, promoções e benefícios.", img: IMAGES.skyline },
  { icon: Sparkles, title: "Serviços e Oportunidades", desc: "Profissionais, soluções e chances perto de você.", img: IMAGES.safetyTech },
  { icon: Truck, title: "Entregas", desc: "Soluções futuras para entregas e logística local.", img: IMAGES.passengerRide },
  { icon: Bike, title: "360Moto", desc: "Futura mobilidade sobre duas rodas.", img: IMAGES.driverNav },
];

export default function Home() {
  const { openPreRegister } = usePreRegister();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.35]);

  return (
    <div className="bg-white">
      {/* ── Bloco 2 — Hero ── */}
      <section ref={heroRef} className="grain relative flex min-h-[100svh] items-center overflow-hidden bg-brand-obsidian" data-testid="home-hero">
        <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
          <img
            src={IMAGES.heroDesktop}
            alt=""
            fetchpriority="high"
            className="hidden h-full w-full scale-110 object-cover object-[50%_72%] md:block"
          />
          <img
            src={IMAGES.heroMobile}
            alt=""
            fetchpriority="high"
            className="block h-full w-full scale-110 object-cover object-[85%_55%] md:hidden"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-obsidian via-brand-obsidian/85 to-brand-obsidian/40" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-brand-obsidian to-transparent" aria-hidden="true" />

        <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-8xl px-4 pb-24 pt-40 sm:px-6 lg:px-8">
          <FadeIn delay={0.1}>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.35em] text-brand-orange sm:text-sm">
              OFF360 · Mobilidade, tecnologia e oportunidades
            </p>
          </FadeIn>

          <MaskLines
            lines={["Conectando você", "ao que importa."]}
            className="mt-6 font-display text-[2.6rem] font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
            delay={0.25}
          />

          <FadeIn delay={0.7}>
            <p className="mt-6 max-w-xl text-lg font-semibold text-brand-amber sm:text-xl">
              Descontos, serviços, entregas e oportunidades perto de você.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              O OFF360 está construindo um novo ecossistema de mobilidade, tecnologia e oportunidades. Nossa primeira
              etapa começa com o 360Taxi. Em breve, novas soluções estarão disponíveis para facilitar ainda mais o
              seu dia a dia.
            </p>
          </FadeIn>

          <FadeIn delay={0.85}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/360taxi"
                data-testid="hero-cta-360taxi"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-8 py-4 text-base font-bold text-white shadow-xl shadow-brand-orange/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-orangedark"
              >
                Conheça o 360Taxi
                <ChevronRight className="h-5 w-5" />
              </Link>
              <button
                type="button"
                data-testid="hero-cta-download"
                onClick={() => openPreRegister({ origem: "home-hero" })}
                className="inline-flex items-center justify-center rounded-full border border-white/30 px-8 py-4 text-base font-bold text-white backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-orange hover:text-brand-orange"
              >
                Baixar aplicativo
              </button>
            </div>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
              Primeira fase · 360Taxi para automóveis
            </p>
          </FadeIn>
        </motion.div>
      </section>

      <Marquee
        items={[
          "360Taxi — Serviço principal",
          "Comércio e descontos em breve",
          "Entregas em breve",
          "Serviços em breve",
          "Oportunidades em breve",
          "360Moto em breve",
        ]}
      />

      {/* ── Bloco 3 — Serviços OFF360 ── */}
      <section className="mx-auto max-w-8xl px-4 py-20 sm:px-6 md:py-28 lg:px-8" data-testid="home-servicos">
        <Reveal>
          <SectionHead
            eyebrow="Serviços OFF360"
            title="Um ecossistema, muitas soluções."
            lead="O lançamento começa pelo 360Taxi. Os demais recursos estão em desenvolvimento e chegarão futuramente."
          />
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.07} className="h-full">
              <div
                data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`flex h-full flex-col gap-4 rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  s.live
                    ? "border-brand-orange/40 bg-gradient-to-b from-brand-orange/10 to-white shadow-lg shadow-brand-orange/10"
                    : "border-slate-200 bg-slate-50 hover:border-slate-300"
                }`}
              >
                <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${s.live ? "bg-brand-orange text-white" : "bg-slate-200 text-slate-500"}`}>
                  <s.icon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-1 text-sm text-slate-500">{s.desc}</p>
                </div>
                <div className="mt-auto pt-2">
                  {s.live ? <BadgeLive>Primeira fase</BadgeLive> : <BadgeSoon />}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-10 rounded-2xl border-l-4 border-brand-orange bg-slate-50 px-6 py-5 text-base font-semibold text-slate-800 sm:text-lg">
            Nosso lançamento começa com o 360Taxi. Os demais recursos estão em desenvolvimento e chegarão
            futuramente.
          </p>
        </Reveal>
      </section>

      {/* ── Bloco 4 — Conheça o 360Taxi ── */}
      <section className="bg-slate-50 py-20 md:py-28" data-testid="home-360taxi">
        <div className="mx-auto grid max-w-8xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div>
            <Reveal>
              <SectionHead
                eyebrow="Conheça o 360Taxi"
                title="360Taxi — Mobilidade para o seu dia a dia."
                lead="O 360Taxi conecta passageiros e motoristas parceiros por meio de uma experiência de mobilidade prática, organizada e tecnológica. Oferece diferentes categorias de corrida e funcionalidades desenvolvidas para trazer mais conforto, transparência e segurança às viagens."
              />
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Reveal delay={0.1}>
                <Link
                  to="/passageiros"
                  data-testid="home-card-passageiro"
                  className="group flex h-full flex-col justify-between gap-6 rounded-2xl bg-brand-obsidian p-7 text-white shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                >
                  <div>
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                      Para você
                    </p>
                    <h3 className="mt-3 font-display text-2xl font-bold">Sou passageiro</h3>
                    <p className="mt-2 text-sm text-slate-300">Conheça como solicitar corridas.</p>
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-bold text-brand-orange">
                    Saiba mais
                    <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
              <Reveal delay={0.18}>
                <Link
                  to="/motoristas"
                  data-testid="home-card-motorista"
                  className="group flex h-full flex-col justify-between gap-6 rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand-orange/50 hover:shadow-xl"
                >
                  <div>
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                      Seja parceiro
                    </p>
                    <h3 className="mt-3 font-display text-2xl font-bold text-slate-900">Quero ser motorista</h3>
                    <p className="mt-2 text-sm text-slate-500">Entenda como funciona o cadastro e a operação.</p>
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-bold text-brand-orange">
                    Saiba mais
                    <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-2xl">
                <img
                  src={IMAGES.passengerRide}
                  alt="Passageiro utilizando o aplicativo de mobilidade dentro do veículo"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                />
              </div>
              <div
                aria-hidden="true"
                className="absolute -bottom-6 -left-6 -z-10 h-40 w-40 rounded-3xl bg-brand-orange/20 blur-2xl"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Bloco 5 — Segurança ── */}
      <section className="grain relative overflow-hidden bg-brand-obsidian py-20 md:py-28" data-testid="home-seguranca">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-0 h-96 w-96 rounded-full opacity-25"
          style={{ background: "radial-gradient(circle, rgba(254,115,13,0.5) 0%, transparent 65%)" }}
        />
        <div className="relative mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHead
              dark
              eyebrow="Segurança e confiança"
              title="Sua segurança faz parte da nossa jornada."
              lead="O 360Taxi reúne recursos de identificação, acompanhamento e suporte para ajudar passageiros e motoristas a realizar viagens com mais tranquilidade."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SAFETY.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col gap-4 rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-brand-orange/50">
                  <f.icon className="h-8 w-8 text-brand-orange" />
                  <div>
                    <h3 className="font-display text-base font-bold text-white sm:text-lg">{f.t}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{f.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <BtnGhost as={Link} to="/seguranca" data-testid="home-cta-seguranca" className="mt-10">
              Conheça nossos recursos de segurança
            </BtnGhost>
          </Reveal>
        </div>
      </section>

      {/* ── Bloco 6 — Futuro OFF360 ── */}
      <section className="mx-auto max-w-8xl px-4 py-20 sm:px-6 md:py-28 lg:px-8" data-testid="home-futuro">
        <Reveal>
          <SectionHead
            eyebrow="Futuro OFF360"
            title="Estamos preparando muito mais para você."
            lead="Nosso ecossistema está crescendo. Em breve, o OFF360 também contará com soluções voltadas ao comércio local, descontos, serviços, entregas, mobilidade sobre duas rodas e novas oportunidades."
          />
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FUTURE.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08} className="h-full">
              <article
                data-testid={`future-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.title}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4">
                    <BadgeSoon />
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-2 p-6">
                  <h3 className="font-display text-lg font-bold text-slate-900">{c.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-500">{c.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <BtnOrange as={Link} to="/futuro-off360" data-testid="home-cta-futuro" className="mt-10">
            Conheça o futuro OFF360
          </BtnOrange>
        </Reveal>
      </section>

      {/* ── Bloco 7 — Chamada de lançamento ── */}
      <section className="px-4 pb-20 sm:px-6 md:pb-28 lg:px-8" data-testid="home-lancamento">
        <Reveal>
          <div className="grain relative mx-auto max-w-8xl overflow-hidden rounded-3xl bg-brand-obsidian px-6 py-16 text-center sm:px-12 md:py-24">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-0 h-72 w-[36rem] -translate-x-1/2 rounded-full opacity-30"
              style={{ background: "radial-gradient(ellipse, rgba(254,115,13,0.55) 0%, transparent 65%)" }}
            />
            <div className="relative">
              <SectionHead
                dark
                center
                eyebrow="Lançamento"
                title="Quer saber quando o aplicativo estará disponível?"
                lead="Cadastre seu nome e e-mail para receber informações sobre a disponibilidade do 360Taxi e os próximos lançamentos do OFF360."
              />
              <BtnOrange
                data-testid="home-cta-pre-cadastro"
                onClick={() => openPreRegister({ origem: "home-lancamento" })}
                className="mt-10"
              >
                Quero ser avisado
              </BtnOrange>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
